import React from 'react';
import { FormBuilder } from '../../../../../components/form/FormBuilder';
import { FormComponent } from '../../../../../components/form/FormComponents';
import { Medication } from '../../../../../interfaces/screens/content/patientDetails';
import dayjs from 'dayjs';
import { store } from '../../../../../redux/store';
import { hideModal } from '../../../../../connected-components/modal/reducer';
import { addMedication } from '../../../medications/addMedication/actions';
import { forms } from '../../../../../shared/static/forms';

interface MedicationFormProps {
  onSubmit: (data: any) => void;
  onCancel: () => void;
  medications: { label: string; value: string }[];
  initialValues?: Partial<Medication>;
  patientId: number;
}

const medicationFormSchema = {
  type: 'object' as const,
  properties: {
    medication: {
      label: 'Medication',
      type: 'text',
      required: true,
      component: FormComponent.SearchableSelect,
      componentProps: {
        options: [], // Will be populated from props
        helperText: 'Search and select medication',
      },
    },
    dosage: {
      label: 'Dosage',
      type: 'text',
      required: true,
      component: FormComponent.Input,
      componentProps: {
        type: 'text',
        placeholder: 'Enter dosage (e.g., 500mg)',
        helperText: 'Specify the amount and unit',
      },
    },
    frequency: {
      label: 'Frequency',
      type: 'text',
      required: true,
      component: FormComponent.Input,
      componentProps: {
        type: 'text',
        placeholder: 'Enter frequency (e.g., twice daily)',
        helperText: 'Specify how often to take the medication',
      },
    },
    startDate: {
      label: 'Start Date',
      type: 'date',
      required: true,
      component: FormComponent.DatePicker,
      componentProps: {
        showTime: false,
        helperText: 'When to start taking the medication',
      },
    },
    notes: {
      label: 'Notes',
      type: 'textarea',
      required: false,
      component: FormComponent.Textarea,
      componentProps: {
        placeholder: 'Enter any additional notes',
        helperText: 'Optional notes about the medication',
      },
    },
  },
  required: ['medication', 'dosage', 'frequency', 'startDate'],
};

export const MedicationForm: React.FC<MedicationFormProps> = ({
  onSubmit,
  onCancel,
  medications,
  initialValues,
  patientId,
}) => {
  // Find the matching medication option for the initial value
  const initialMedication = initialValues?.name 
    ? medications.find(med => med.label === initialValues.name)?.value 
    : '';

  // Update the schema with the provided medications
  const schema = {
    ...medicationFormSchema,
    properties: {
      ...medicationFormSchema.properties,
      medication: {
        ...medicationFormSchema.properties.medication,
        componentProps: {
          ...medicationFormSchema.properties.medication.componentProps,
          options: medications,
        },
      },
    },
  };

  // Format initial values for the form
  const formattedInitialValues = {
    medication: initialMedication,
    dosage: initialValues?.dosage || '',
    frequency: initialValues?.frequency || '',
    startDate: initialValues?.startDate ? dayjs(initialValues.startDate) : null,
    notes: initialValues?.notes || '',
  };

  const handleSubmit = async (values: Record<string, any>) => {
    onSubmit(values);
  };

  return (
    <div className="py-4">
      <FormBuilder
        schema={forms.addNewMedication}
        onSubmit={handleSubmit}
        initialValues={formattedInitialValues}
        className="space-y-4"
      />
    </div>
  );
}; 